import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'

import { Checkbox, Flex, Text, Tooltip } from '@chakra-ui/react'

import { FacilityMatchedFieldT, OptionI } from '@/utils/forms/optionBuilders'

interface MultiSelectCheckboxIProps {
  isChecked: boolean
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  // subtitle/matchedField are optional extras a facility-backed option can
  // carry (see getIncidentFacilityOptionsWithAddress) — every other consumer
  // of this shared checkbox (Guard Name, Device Name, Status, Action, ...)
  // passes plain {label, value} and simply never renders these lines.
  option: OptionI & { subtitle?: string; matchedField?: FacilityMatchedFieldT }
}

export const MultiSelectCheckbox = ({
  isChecked,
  onChange,
  option,
}: MultiSelectCheckboxIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()
  const ref = useRef(null)
  const [isOverflown, setIsOverflown] = useState(false)
  useEffect(() => {
    const element = ref.current!
    setIsOverflown(
      element
        ? element?.offsetWidth < element?.scrollWidth ||
            element?.offsetHeight < element?.scrollHeight
        : false
    )
  }, [])
  return (
    <Tooltip
      isDisabled={!isOverflown}
      label={option.label ?? 'Unknown Option'}
      openDelay={500}
      shouldWrapChildren
    >
      <Flex alignItems='center' dir='row' gap='10px' mb='10px'>
        <Checkbox isChecked={isChecked} onChange={onChange}>
          <Text fontSize='14px' fontWeight='500' noOfLines={2} ref={ref}>
            {option.label ?? 'Unknown Option'}
          </Text>
          {option.subtitle && (
            <Text color={`text.secondary.${mode}`} fontSize='12px'>
              {option.subtitle}
            </Text>
          )}
          {option.matchedField === 'address' && (
            <Text
              color={`text.tertiary.${mode}`}
              fontSize='11px'
              fontStyle='italic'
            >
              {t('common:facility_matched_by_address')}
            </Text>
          )}
        </Checkbox>
      </Flex>
    </Tooltip>
  )
}
